import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import { 
  Plus, Search, CheckCircle2, Circle, Clock, AlertCircle,
  ChevronDown, ChevronRight, User, Filter, RefreshCw, Link, Copy, PlayCircle, Eye, ThumbsUp
} from 'lucide-react';
import { toast } from 'sonner';

const PRIORITIES = [
  { value: 'high', label: 'High', color: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  { value: 'low', label: 'Low', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' }
];

const TASK_TYPE_PRESETS = [
  'Colour Corrected Images',
  'Edited Images',
  'Teaser Video',
  'Highlight & Reels',
  'Performance Video',
  'Full Film / Full Video'
];

const TasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(() => ((user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r)) ? 'all-tasks' : 'my-tasks'));
  const [newTask, setNewTask] = useState({
    project_id: '',
    title: '',
    task_type: '',
    description: '',
    assigned_to: '',
    due_date: '',
    priority: 'medium'
  });
  const [expandedProjects, setExpandedProjects] = useState({});
  const [showDeliverableDialog, setShowDeliverableDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [deliverableLink, setDeliverableLink] = useState('');

  const isManager = (user?.roles || [user?.role]).some(r => ['admin', 'manager'].includes(r));

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [projectsRes, teamRes] = await Promise.all([
        api.get('/projects'),
        api.get('/users/team')
      ]);
      setProjects(projectsRes.data);
      setTeamMembers(teamRes.data);
      
      // Fetch tasks based on active tab
      if (activeTab === 'my-tasks') {
        const tasksRes = await api.get('/tasks/my-tasks');
        setMyTasks(tasksRes.data);
      } else if (activeTab === 'all-tasks' && isManager) {
        const tasksRes = await api.get('/tasks/all-tasks');
        setAllTasks(tasksRes.data);
      } else {
        const tasksRes = await api.get('/tasks');
        setTasks(tasksRes.data);
      }
      
      // Auto-expand all projects initially
      const expanded = {};
      projectsRes.data.forEach(p => { expanded[p.id] = true; });
      setExpandedProjects(expanded);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) { toast.error('Enter a task title'); return; }
    try {
      const res = await api.post('/tasks', {
        ...newTask,
        project_id: newTask.project_id || null,  // standalone tasks allowed
        due_date: newTask.due_date ? new Date(newTask.due_date).toISOString() : null,
      });
      toast.success(newTask.project_id ? 'Task created' : 'Standalone task created');
      // Send email notification
      if (newTask.assigned_to && res.data?.id) {
        api.post(`/notifications/send-task-assigned?task_id=${res.data.id}`).catch(() => {});
      }
      setShowCreateDialog(false);
      setNewTask({ project_id: '', title: '', task_type: '', description: '', assigned_to: '', due_date: '', priority: 'medium' });
      fetchData();
    } catch (err) {
      toast.error('Failed to create task');
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: newStatus });
      toast.success(`Task ${newStatus.replace(/_/g, ' ')}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update task');
    }
  };

  const handleSaveDeliverable = async () => {
    try {
      await api.patch(`/tasks/${selectedTask.id}`, { deliverable_link: deliverableLink, status: 'done' });
      toast.success('Task completed with deliverable link');
      setShowDeliverableDialog(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied!');
  };

  const openComplete = (task) => {
    setSelectedTask(task);
    setDeliverableLink(task.deliverable_link || '');
    setShowDeliverableDialog(true);
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({...prev, [projectId]: !prev[projectId]}));
  };

  const getCurrentTasks = () => {
    if (activeTab === 'my-tasks') return myTasks;
    if (activeTab === 'all-tasks') return allTasks;
    return tasks;
  };

  const filteredTasks = getCurrentTasks().filter(t => {
    const matchesSearch = t.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchesProject = projectFilter === 'all' || t.project_id === projectFilter;
    const matchesAssigned = assignedToFilter === 'all' || (assignedToFilter === '_none' ? !t.assigned_to : t.assigned_to === assignedToFilter);
    return matchesSearch && matchesStatus && matchesProject && matchesAssigned;
  });

  // Group tasks by project
  const tasksByProject = {};
  filteredTasks.forEach(task => {
    if (!tasksByProject[task.project_id]) {
      tasksByProject[task.project_id] = [];
    }
    tasksByProject[task.project_id].push(task);
  });

  const getPriorityColor = (priority) => {
    return PRIORITIES.find(p => p.value === priority)?.color || PRIORITIES[2].color;
  };

  const getStatusIcon = (status) => {
    if (status === 'done') return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
    if (status === 'review') return <Eye className="h-4 w-4 text-purple-400" />;
    if (status === 'in_progress') return <PlayCircle className="h-4 w-4 text-blue-400" />;
    if (status === 'accepted') return <ThumbsUp className="h-4 w-4 text-cyan-400" />;
    return <Circle className="h-4 w-4 text-zinc-500" />;
  };

  const getStatusLabel = (status) => {
    const labels = { todo: 'To Do', accepted: 'Accepted', in_progress: 'In Progress', review: 'In Review', done: 'Done' };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      todo: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
      accepted: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
      in_progress: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      review: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
      done: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    return colors[status] || colors.todo;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">Tasks</h1>
          <p className="text-zinc-500 text-sm mt-1">
            {filteredTasks.filter(t => t.status !== 'done').length} pending tasks
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}
            className="border-[#F5A623]/30 text-[#F5A623] hover:bg-[#F5A623]/10" data-testid="refresh-tasks">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="create-task-btn">
            <Plus className="h-4 w-4 mr-2" /> New Task
          </Button>
        </div>
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* Brand Filter */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Brand</Label>
                <div className="flex gap-2 mt-1">
                  {[{v: 'all', l: 'All'}, {v: 'wittyshadows', l: 'Wittyshadows'}, {v: 'triobliss', l: 'Trio Bliss'}].map(b => (
                    <button key={b.v} type="button" onClick={() => setNewTask({...newTask, _brand: b.v, project_id: ''})}
                      className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                        (newTask._brand || 'all') === b.v
                          ? b.v === 'triobliss' ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                            : b.v === 'wittyshadows' ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30'
                            : 'bg-white/10 text-white border border-white/20'
                          : 'bg-[#0A0A0A] text-zinc-400 border border-white/10 hover:border-white/20'
                      }`}>
                      {b.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Project Selection */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Project (optional)</Label>
                <div className="max-h-32 overflow-y-auto space-y-1 bg-[#0A0A0A] rounded-md border border-white/10 p-2 mt-1">
                  <button type="button" onClick={() => setNewTask({...newTask, project_id: ''})}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-left text-xs rounded transition-colors ${
                      !newTask.project_id ? 'bg-zinc-500/20 text-zinc-200' : 'text-zinc-400 hover:bg-white/10'
                    }`} data-testid="select-no-project">
                    <span className="italic">No project — standalone task</span>
                  </button>
                  {projects
                    .filter(p => !newTask._brand || newTask._brand === 'all' || (p.brand || 'wittyshadows') === newTask._brand)
                    .map(p => (
                    <button key={p.id} type="button" onClick={() => setNewTask({...newTask, project_id: p.id})}
                      className={`w-full flex items-center justify-between px-2 py-1.5 text-left text-xs rounded transition-colors ${
                        newTask.project_id === p.id ? 'bg-[#F5A623]/20 text-[#F5A623]' : 'text-zinc-300 hover:bg-white/10'
                      }`} data-testid={`select-project-${p.id}`}>
                      <span>{p.title}</span>
                      <Badge variant="outline" className={`text-[9px] ${(p.brand || 'wittyshadows') === 'triobliss' ? 'bg-pink-500/10 text-pink-400 border-pink-500/20' : 'bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/20'}`}>
                        {(p.brand || 'wittyshadows') === 'triobliss' ? 'TB' : 'WS'}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>

              {/* Task Type Presets */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Task Type</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {TASK_TYPE_PRESETS.map(type => (
                    <button key={type} type="button" onClick={() => setNewTask({...newTask, title: type, task_type: type})}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        newTask.title === type ? 'bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30' : 'bg-[#0A0A0A] text-zinc-400 border border-white/10 hover:border-white/20'
                      }`}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Task Title */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Task Title *</Label>
                <Input value={newTask.title} onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Or type a custom task name..." data-testid="task-title-input" />
              </div>

              {/* Description */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Description</Label>
                <Textarea value={newTask.description} onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="Add more details..." rows={2} />
              </div>

              {/* Assign To - Button list */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Assign To</Label>
                <div className="max-h-32 overflow-y-auto space-y-1 bg-[#0A0A0A] rounded-md border border-white/10 p-2 mt-1">
                  {teamMembers.map(m => (
                    <button key={m.id} type="button" onClick={() => setNewTask({...newTask, assigned_to: m.id})}
                      className={`w-full flex items-center justify-between px-2 py-1.5 text-left text-xs rounded transition-colors ${
                        newTask.assigned_to === m.id ? 'bg-[#F5A623]/20 text-[#F5A623]' : 'text-zinc-300 hover:bg-white/10'
                      }`} data-testid={`assign-${m.id}`}>
                      <span>{m.name}</span>
                      <span className="text-[10px] text-zinc-600 capitalize">{(m.roles || [m.role]).join(', ').replace(/_/g, ' ')}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority - Button group */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Priority</Label>
                <div className="flex gap-2 mt-1">
                  {PRIORITIES.map(p => (
                    <button key={p.value} type="button" onClick={() => setNewTask({...newTask, priority: p.value})}
                      className={`flex-1 py-2 rounded-md text-xs font-medium transition-colors ${
                        newTask.priority === p.value ? p.color + ' border' : 'bg-[#0A0A0A] text-zinc-400 border border-white/10 hover:border-white/20'
                      }`} data-testid={`priority-${p.value}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Due Date */}
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Due Date</Label>
                <Input type="date" value={newTask.due_date} onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  className="bg-[#0A0A0A] border-white/10 text-white mt-1" data-testid="task-due-date" />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
                <Button onClick={handleCreateTask} className="bg-[#F5A623] hover:bg-[#F5A623]/90 text-black" data-testid="submit-task-btn">Create Task</Button>
              </div>
            </div>
          </DialogContent>
      </Dialog>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#121212] border border-white/10 p-1">
          <TabsTrigger 
            value="my-tasks" 
            className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
            data-testid="my-tasks-tab"
          >
            My Tasks
          </TabsTrigger>
          {isManager && (
            <TabsTrigger 
              value="all-tasks" 
              className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
              data-testid="all-tasks-tab"
            >
              All Tasks
            </TabsTrigger>
          )}
          <TabsTrigger 
            value="by-project" 
            className="data-[state=active]:bg-[#F5A623] data-[state=active]:text-black text-zinc-400"
            data-testid="by-project-tab"
          >
            By Project
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[#121212] border-white/10 text-white placeholder:text-zinc-500"
            data-testid="search-tasks-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-[#121212] border-white/10 text-white">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All Status</SelectItem>
            <SelectItem value="todo" className="text-white hover:bg-white/10">To Do</SelectItem>
            <SelectItem value="accepted" className="text-white hover:bg-white/10">Accepted</SelectItem>
            <SelectItem value="in_progress" className="text-white hover:bg-white/10">In Progress</SelectItem>
            <SelectItem value="review" className="text-white hover:bg-white/10">In Review</SelectItem>
            <SelectItem value="done" className="text-white hover:bg-white/10">Done</SelectItem>
          </SelectContent>
        </Select>
        {isManager && activeTab !== 'my-tasks' && (
          <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-[#121212] border-white/10 text-white">
              <User className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Assigned To" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              <SelectItem value="all" className="text-white hover:bg-white/10">All People</SelectItem>
              {teamMembers.map(m => (
                <SelectItem key={m.id} value={m.id} className="text-white hover:bg-white/10">{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-full sm:w-48 bg-[#121212] border-white/10 text-white">
            <SelectValue placeholder="Project" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="all" className="text-white hover:bg-white/10">All Projects</SelectItem>
            {projects.map(p => (
              <SelectItem key={p.id} value={p.id} className="text-white hover:bg-white/10">{p.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isManager && activeTab !== 'my-tasks' && (() => {
        const by = {};
        getCurrentTasks().forEach(t => {
          const k = t.assigned_to || '_none';
          by[k] = by[k] || { name: t.assigned_to_name || 'Unassigned', open: 0, done: 0 };
          if (t.status === 'done') by[k].done++; else by[k].open++;
        });
        return (
          <div className="flex flex-wrap gap-2" data-testid="assignee-summary">
            {Object.entries(by).sort((a, b) => b[1].open - a[1].open).map(([id, v]) => (
              <button key={id} type="button"
                onClick={() => setAssignedToFilter(assignedToFilter === id ? 'all' : id)}
                className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${assignedToFilter === id ? 'bg-[#F5A623]/20 text-[#F5A623] border-[#F5A623]/30' : 'bg-[#121212] text-zinc-300 border-white/10 hover:border-white/20'}`}>
                {v.name} · {v.open} open · {v.done} done
              </button>
            ))}
          </div>
        );
      })()}

      {/* Tasks List - grouped by project for "by-project" tab */}
      {activeTab === 'by-project' ? (
        <div className="space-y-4" data-testid="tasks-by-project">
          {Object.entries(tasksByProject).map(([projectId, projectTasks]) => {
            const project = projects.find(p => p.id === projectId);
            const isExpanded = expandedProjects[projectId];
            const completedCount = projectTasks.filter(t => t.status === 'done').length;
            
            return (
              <Card key={projectId} className="bg-[#121212] border-white/10">
                <CardHeader 
                  className="cursor-pointer py-3"
                  onClick={() => toggleProject(projectId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? 
                        <ChevronDown className="h-4 w-4 text-zinc-500" /> : 
                        <ChevronRight className="h-4 w-4 text-zinc-500" />
                      }
                      <div>
                        <CardTitle className="text-white text-base">
                          {project?.title || 'Unknown Project'}
                        </CardTitle>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {completedCount}/{projectTasks.length} tasks completed
                        </p>
                      </div>
                    </div>
                    <div className="h-2 w-24 bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-emerald-500 transition-all"
                        style={{ width: `${projectTasks.length > 0 ? (completedCount / projectTasks.length) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="pt-0 pb-4">
                    <div className="space-y-2">
                      {projectTasks.map(task => (
                        <TaskItem 
                          key={task.id} 
                          task={task} 
                          onStatusChange={handleStatusChange}
                          onComplete={openComplete}
                          onCopyLink={copyLink}
                          getPriorityColor={getPriorityColor}
                          getStatusIcon={getStatusIcon}
                          getStatusLabel={getStatusLabel}
                          getStatusColor={getStatusColor}
                          formatDate={formatDate}
                          isOverdue={isOverdue}
                          showAssignee={isManager}
                          isManager={isManager}
                        />
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        // Flat list for My Tasks and All Tasks
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="space-y-2" data-testid="tasks-list">
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No tasks found</p>
                </div>
              ) : (
                filteredTasks.map(task => (
                  <TaskItem 
                    key={task.id} 
                    task={task} 
                    onStatusChange={handleStatusChange}
                    onComplete={openComplete}
                    onCopyLink={copyLink}
                    getPriorityColor={getPriorityColor}
                    getStatusIcon={getStatusIcon}
                    getStatusLabel={getStatusLabel}
                    getStatusColor={getStatusColor}
                    formatDate={formatDate}
                    isOverdue={isOverdue}
                    showProject={true}
                    showAssignee={isManager}
                    isManager={isManager}
                  />
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Complete with Deliverable Dialog */}
      <Dialog open={showDeliverableDialog} onOpenChange={setShowDeliverableDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-zinc-400">{selectedTask?.title} — {selectedTask?.project_title}</p>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Deliverable Link (Drive / File URL)</Label>
              <Input value={deliverableLink} onChange={e => setDeliverableLink(e.target.value)}
                className="bg-[#0A0A0A] border-white/10 text-white mt-1" placeholder="https://drive.google.com/..." data-testid="deliverable-link-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowDeliverableDialog(false)} className="border-white/10 text-white hover:bg-white/5">Cancel</Button>
              <Button onClick={handleSaveDeliverable} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="complete-task-btn">Mark Complete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== TASK ITEM COMPONENT ====================

const TaskItem = ({ task, onStatusChange, onComplete, onCopyLink, getPriorityColor, getStatusIcon, getStatusLabel, getStatusColor, formatDate, isOverdue, showProject = false, showAssignee = false, isManager = false }) => {
  const nextStatus = () => {
    if (task.status === 'todo') return 'accepted';
    if (task.status === 'accepted') return 'in_progress';
    if (task.status === 'in_progress') return 'review';
    return null;
  };

  const nextAction = nextStatus();

  return (
    <div className={`p-3 rounded-lg border border-white/5 hover:border-white/10 transition-colors ${task.status === 'done' ? 'opacity-60' : ''}`}
      data-testid={`task-item-${task.id}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">{getStatusIcon(task.status)}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className={`text-sm font-medium ${task.status === 'done' ? 'text-zinc-500 line-through' : 'text-white'}`}>
              {task.title}
            </p>
            <Badge variant="outline" className={`text-[10px] ${getStatusColor(task.status)}`}>
              {getStatusLabel(task.status)}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
            {showProject && task.project_title && <span>{task.project_title}</span>}
            {showAssignee && task.assigned_to_name && (
              <span className="flex items-center gap-1"><User className="h-3 w-3" />{task.assigned_to_name}</span>
            )}
            {task.time_spent_hours > 0 && (
              <span className="text-emerald-400">{task.time_spent_hours}h spent</span>
            )}
          </div>
          {/* Deliverable link */}
          {task.deliverable_link && task.status === 'done' && (
            <div className="flex items-center gap-2 mt-1.5">
              <Link className="h-3 w-3 text-blue-400" />
              <a href={task.deliverable_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 truncate max-w-[200px]">
                {task.deliverable_link}
              </a>
              <button onClick={() => onCopyLink(task.deliverable_link)} className="p-1 text-zinc-400 hover:text-white rounded hover:bg-white/10 transition-colors" data-testid={`copy-link-${task.id}`}>
                <Copy className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Badge variant="outline" className={`text-[10px] ${getPriorityColor(task.priority)}`}>
            {task.priority}
          </Badge>
          {task.due_date && (
            <span className={`text-xs ${isOverdue(task.due_date) && task.status !== 'done' ? 'text-rose-400' : 'text-zinc-500'}`}>
              {isOverdue(task.due_date) && task.status !== 'done' && <AlertCircle className="h-3 w-3 inline mr-0.5" />}
              {formatDate(task.due_date)}
            </span>
          )}
          {/* Action buttons */}
          {task.status !== 'done' && (
            <div className="flex gap-1">
              {nextAction && (
                <Button size="sm" variant="outline" onClick={() => onStatusChange(task, nextAction)}
                  className="h-7 px-2 text-xs border-white/10 text-zinc-300 hover:bg-white/5" data-testid={`action-${task.id}`}>
                  {nextAction === 'accepted' && 'Accept'}
                  {nextAction === 'in_progress' && 'Start'}
                  {nextAction === 'review' && 'Submit'}
                </Button>
              )}
              {task.status === 'review' && (
                <Button size="sm" onClick={() => onComplete(task)}
                  className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`complete-${task.id}`}>
                  Complete
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TasksPage;
